import { forwardRef, Fragment, useState, type HTMLAttributes, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Breadcrumbs>. Collapsing
// logic (first item + last 2 once `maxItems` is exceeded) and CSS ported
// letter-for-letter from the vendor's compiled bundle and its Breadcrumbs
// CSS — new .rc-breadcrumbs class names. Default separator icon switched
// from @datasirpi/icons' direction-agnostic ChevronEnd to lucide's
// ChevronRight: this app has no RTL support anywhere (confirmed — no
// `dir="rtl"` usage exists), so they render identically here, and Lucide
// is design.md's icon system.
export interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
}

export interface BreadcrumbsProps extends Omit<HTMLAttributes<HTMLElement>, "children"> {
  items: ReadonlyArray<BreadcrumbItem>;
  "aria-label"?: string;
  maxItems?: number;
  separator?: ReactNode;
  renderLink?: (item: BreadcrumbItem) => ReactNode;
}

function defaultRenderLink(item: BreadcrumbItem) {
  return (
    <a href={item.href} className="rc-breadcrumbs__link">
      {item.label}
    </a>
  );
}

type VisibleEntry = { kind: "item"; item: BreadcrumbItem; index: number } | { kind: "ellipsis" };

export const Breadcrumbs = forwardRef<HTMLElement, BreadcrumbsProps>(function Breadcrumbs(
  { items, "aria-label": ariaLabel = "Breadcrumb", maxItems = Number.POSITIVE_INFINITY, separator, renderLink = defaultRenderLink, className, ...rest },
  ref
) {
  const sep = separator ?? <ChevronRight size={14} aria-hidden="true" />;
  // d2d-DetectHub eval F16: the ellipsis was a labelled <span>, which says
  // "More crumbs" and then offers no way to see them. It is a button now.
  const [expanded, setExpanded] = useState(false);
  const visible: VisibleEntry[] = [];
  if (expanded || items.length <= maxItems) {
    items.forEach((item, i) => visible.push({ kind: "item", item, index: i }));
  } else {
    visible.push({ kind: "item", item: items[0], index: 0 });
    visible.push({ kind: "ellipsis" });
    const tail = items.slice(-2);
    tail.forEach((item, i) => visible.push({ kind: "item", item, index: items.length - tail.length + i }));
  }

  return (
    <nav ref={ref} aria-label={ariaLabel} className={cn("rc-breadcrumbs", className)} {...rest}>
      <ol className="rc-breadcrumbs__list">
        {visible.map((entry, i) => {
          const isLast = i === visible.length - 1;
          if (entry.kind === "ellipsis") {
            return (
              <Fragment key={`ellipsis-${i}`}>
                <li className="rc-breadcrumbs__item">
                  <button type="button" className="rc-breadcrumbs__ellipsis" aria-label="Show all breadcrumbs" onClick={() => setExpanded(true)}>
                    …
                  </button>
                </li>
                <li className="rc-breadcrumbs__separator" aria-hidden="true">
                  {sep}
                </li>
              </Fragment>
            );
          }
          const { item } = entry;
          const isCurrent = isLast || item.href === undefined;
          return (
            <Fragment key={entry.index}>
              <li className="rc-breadcrumbs__item">
                {isCurrent ? (
                  <span aria-current="page" className="rc-breadcrumbs__current">
                    {item.label}
                  </span>
                ) : (
                  renderLink(item)
                )}
              </li>
              {!isLast ? (
                <li className="rc-breadcrumbs__separator" aria-hidden="true">
                  {sep}
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
});
